import React, { useRef, useEffect } from "react";
import "./action-input.scss";

export interface ActionInputProps {
  value: string;
  onChange?: (
    value: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAction?: (
    value: string,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  loading?: boolean;
  iconColor?: string;
  iconSize?: number;
  height?: string;
  dirtyColor?: string;
  className?: string;
}

export const ActionInput: React.FC<ActionInputProps> = ({
  value,
  onChange,
  onAction,
  placeholder,
  readOnly = false,
  disabled = false,
  loading = false,
  iconColor = "#6B6B6B",
  iconSize = 16,
  height,
  dirtyColor = "#0056D6",
  className = "",
}) => {
  const initialValueRef = useRef(value);
  const isDirty = value !== initialValueRef.current;

  useEffect(() => {
    initialValueRef.current = value;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value, e);
  };

  const handleAction = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onAction?.(value, e);
  };

  const activeIconColor = isDirty ? dirtyColor : iconColor;

  return (
    <div
      className={`action-input ${disabled ? "action-input--disabled" : ""} ${className}`}
      style={height ? { height } : undefined}
    >
      <input
        className="action-input__input"
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={disabled}
      />
      <button
        className="action-input__btn"
        onClick={handleAction}
        type="button"
        disabled={disabled || loading}
        aria-label={loading ? "Loading" : "Submit value"}
      >
        {loading ? (
          <span
            className="action-input__loader"
            style={{ width: iconSize, height: iconSize, borderColor: `${activeIconColor}33`, borderTopColor: activeIconColor }}
          />
        ) : (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 11.0857V12.0057C21.9988 14.1621 21.3005 16.2604 20.0093 17.9875C18.7182 19.7147 16.9033 20.9782 14.8354 21.5896C12.7674 22.201 10.5573 22.1276 8.53447 21.3803C6.51168 20.633 4.78465 19.2518 3.61096 17.4428C2.43727 15.6338 1.87979 13.4938 2.02168 11.342C2.16356 9.19029 2.99721 7.14205 4.39828 5.50172C5.79935 3.86139 7.69279 2.71439 9.79619 2.24015C11.8996 1.76591 14.1003 1.98976 16.07 2.87572M22 4L12 14.01L9 11.01"
              stroke={activeIconColor}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </div>
  );
};
